import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, formatDate, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Boxes, Trash2, Filter } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CATEGORY_LABEL = {
  welding_rod: "Welding Rod",
  abrasive: "Abrasive",
  cement: "Cement",
  diesel: "Diesel",
  other: "Other",
};

const CATEGORY_OPTIONS = ["all", ...Object.keys(CATEGORY_LABEL)];

export default function IndustrialStock() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stock, setStock] = useState([]);
  const [txns, setTxns] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [logType, setLogType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayISO());

  const loadStock = async () => {
    try {
      const { data } = await api.get("/industrial/stock");
      setStock(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadTxns = async () => {
    try {
      const params = new URLSearchParams();
      if (logType !== "all") params.set("type", logType);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(`/industrial/transactions?${params.toString()}`);
      setTxns(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadStock(); }, []);
  useEffect(() => { loadTxns(); }, [logType, fromDate, toDate]);

  const filteredStock = useMemo(() =>
    filterCat === "all" ? stock : stock.filter((s) => s.category === filterCat)
  , [stock, filterCat]);

  const stockSummary = useMemo(() => {
    const byCat = {};
    for (const s of stock) {
      if (!byCat[s.category]) byCat[s.category] = { items: 0, low: 0 };
      byCat[s.category].items += 1;
      if (s.current_stock <= 0) byCat[s.category].low += 1;
    }
    return byCat;
  }, [stock]);

  const delTxn = async (id) => {
    if (!window.confirm("Delete this transaction? Stock will re-compute.")) return;
    try {
      await api.delete(`/industrial/transactions/${id}`);
      toast.success("Deleted");
      loadStock(); loadTxns();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="industrial-stock-page">
      <p className="small-label text-gray-500 flex items-center gap-2">
        <Boxes size={14} /> Live stock of industrial supplies. Purchases add, sales reduce.
      </p>

      {/* Category summary chips */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(stockSummary).map(([cat, s]) => (
          <div key={cat} className="px-3 py-2 bg-white border border-gray-200 rounded-sm text-xs">
            <span className="font-heading font-bold">{CATEGORY_LABEL[cat] || cat}</span>
            <span className="ml-2 text-gray-500">{s.items} items</span>
            {s.low > 0 && <span className="ml-2 text-red-600 font-semibold">{s.low} low/out</span>}
          </div>
        ))}
      </div>

      {/* Current Stock */}
      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
          <div className="font-heading font-bold text-lg">Current Stock ({filteredStock.length})</div>
          <select className="input-lg" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="ind-stock-filter-cat">
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c === "all" ? "All Categories" : CATEGORY_LABEL[c]}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left small-label text-gray-500">
              <tr>
                <th className="px-4 py-2">Item</th>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2 text-right">Purchased</th>
                <th className="px-4 py-2 text-right">Sold</th>
                <th className="px-4 py-2 text-right">Current Stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStock.map((s) => {
                const low = s.current_stock <= 0;
                return (
                  <tr key={s.id} data-testid={`ind-stock-row-${s.id}`} className={low ? "bg-red-50" : ""}>
                    <td className="px-4 py-2 font-semibold">{s.name}</td>
                    <td className="px-4 py-2">{s.brand || <span className="text-gray-400">—</span>}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 rounded-sm bg-gray-100 text-xs font-semibold">
                        {CATEGORY_LABEL[s.category] || s.category}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right data-num">{s.total_purchased} <span className="text-xs text-gray-500 uppercase">{s.unit}</span></td>
                    <td className="px-4 py-2 text-right data-num">{s.total_sold} <span className="text-xs text-gray-500 uppercase">{s.unit}</span></td>
                    <td className={`px-4 py-2 text-right data-num font-bold ${low ? "text-red-700" : "text-green-700"}`}>
                      {s.current_stock} <span className="text-xs uppercase">{s.unit}</span>
                    </td>
                  </tr>
                );
              })}
              {filteredStock.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">No items. Add items first from Industrial Items page.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Log */}
      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
          <div className="font-heading font-bold text-lg flex items-center gap-2">
            <Filter size={16} /> Transactions ({txns.length})
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select className="input-lg" value={logType} onChange={(e) => setLogType(e.target.value)} data-testid="ind-log-type">
              <option value="all">All Types</option>
              <option value="purchase">Purchase</option>
              <option value="sale">Sale</option>
            </select>
            <input type="date" className="input-lg" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="ind-log-from" />
            <input type="date" className="input-lg" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="ind-log-to" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left small-label text-gray-500">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Party</th>
                <th className="px-4 py-2">Bill</th>
                <th className="px-4 py-2">Items</th>
                <th className="px-4 py-2 text-right">Total ₹</th>
                {isAdmin && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {txns.map((t) => (
                <tr key={t.id} data-testid={`ind-txn-row-${t.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${t.type === "purchase" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                      {t.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2">{t.party_name || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">{t.bill_no || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">
                    <div className="space-y-0.5">
                      {(t.items || []).map((it, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="font-semibold">{it.name}</span>
                          {it.brand ? ` · ${it.brand}` : ""} — <span className="data-num">{it.qty} {it.unit}</span> @ ₹{formatMoney(it.rate)}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right data-num font-semibold">₹ {formatMoney(t.total_amount)}</td>
                  {isAdmin && (
                    <td className="px-4 py-2 text-right">
                      <button className="text-red-600 hover:text-red-800" onClick={() => delTxn(t.id)}
                        data-testid={`ind-txn-del-${t.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-8 text-center text-gray-400 text-sm">No transactions in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
